import { useMutation, useQueryClient } from 'react-query';
import { getInvoiceDataUseCases } from '@subscriptions/composition';
import type { UpdateInvoiceDataDto, InvoiceDataViewDto } from '@subscriptions/application/dtos';

/** Variables accepted by the update invoice data mutation */
export type UpdateInvoiceDataVariables = {
  uuid: string;
  dto: UpdateInvoiceDataDto;
  /** Reaches a record deleted after its payment was invoiced; only the retry flow sets it. */
  includeDeleted?: boolean;
};

/**
 * Hook for updating invoice data (datos fiscales).
 *
 * Invalidates the invoice data list cache on success so any consumer refetches.
 *
 * @returns Mutation result for updating invoice data
 */
export const useUpdateInvoiceData = () => {
  const queryClient = useQueryClient();
  const { updateInvoiceData } = getInvoiceDataUseCases();

  return useMutation<InvoiceDataViewDto, Error, UpdateInvoiceDataVariables>(
    ({ uuid, dto, includeDeleted }) => updateInvoiceData.execute(uuid, dto, includeDeleted),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['subscriptions', 'invoice-data', 'list']);
      },
    },
  );
};
